import { Alert, Badge, Button, Group, Modal, Stack, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconClock, IconX } from "@tabler/icons-react";
import { useMemo, useState } from "react";

import { ZBoardTemplateStatus } from "@mcapps/shared";
import { z } from "zod";
import { usePatchBoardsTemplateActionMutation } from "../../../../../openapi";
import { selectCurrentProjectId } from "../../../../global/globalSliceCommon";
import { useAppSelector } from "../../../../hooks";
import { selectHttpIssueBoard } from "../redux/selectors/issueRtkSelectors";
import { isDeveloper } from "../../../utils";
import "../issueboardapi.enhance";

type TemplateStatus = z.infer<typeof ZBoardTemplateStatus>;

const getStatusColor = (status: TemplateStatus) => {
  return status === "AKTIV" ? "green" : "gray";
};

const IssueBoardMcTemplateBanner: React.FC = () => {
  const issueBoard = useAppSelector(selectHttpIssueBoard);
  const projectId = useAppSelector(selectCurrentProjectId);
  const userId = useAppSelector((state) => state.globalReducer.user?._id);

  const [opened, setOpened] = useState(false);
  const [patchAction, { isLoading: isPatching }] = usePatchBoardsTemplateActionMutation();

  const currentStatus = issueBoard.template.status != null ? issueBoard.template.status : "IKKE AKTIV";
  const targetStatus = useMemo<TemplateStatus>(
    () => (currentStatus === "AKTIV" ? "IKKE AKTIV" : "AKTIV"),
    [currentStatus],
  );
  const approvedBy = issueBoard.template.approvedBy;
  const approvalDate = issueBoard.template.approvalDate
    ? new Date(issueBoard.template.approvalDate).toLocaleDateString("nb-NO")
    : null;
  const isActive = currentStatus === "AKTIV";
  const canChangeStatus = userId != null && isDeveloper(userId);

  const closeModal = () => {
    setOpened(false);
  };

  const handleSubmit = async () => {
    if (!projectId) {
      notifications.show({
        title: "Feil",
        message: "Mangler prosjekt for statusendring",
        color: "red",
        icon: <IconX size={16} />,
      });
      return;
    }

    try {
      await patchAction({
        body: {
          projectId,
          boardId: issueBoard._id,
          action: targetStatus,
        },
      }).unwrap();

      notifications.show({
        title: "Status oppdatert",
        message: `Malen er nå satt til ${targetStatus}`,
        color: "green",
        icon: <IconCheck size={16} />,
      });

      closeModal();
    } catch (err: any) {
      notifications.show({
        title: "Feil",
        message: err?.data?.message || err?.data?.error?.message || "Kunne ikke oppdatere status",
        color: "red",
        icon: <IconX size={16} />,
      });
    }
  };

  return (
    <>
      <Alert
        color={getStatusColor(currentStatus)}
        variant="light"
        bg={isActive ? "green.2" : "yellow.2"}
        radius={0}
        p="sm"
        m="xs"
      >
        <Group justify="space-between" wrap="wrap">
          <Group gap="sm">
            {isActive ? <IconCheck size={20} /> : <IconClock size={20} />}
            <Text fw={600} size="sm">
              Malstatus:
            </Text>
            <Badge size="sm" color={getStatusColor(currentStatus)} variant="filled">
              {currentStatus}
            </Badge>
            {isActive && approvedBy && (
              <Text size="sm" c="dimmed">
                Aktivert av <strong>{approvedBy.name}</strong> den {approvalDate}
              </Text>
            )}
            {!isActive && (
              <Text size="sm" c="dimmed">
                Denne malen er ikke aktiv enda. Kontakt mcapps@multiconsult.no for å få den aktivert.
              </Text>
            )}
          </Group>
          {canChangeStatus && (
            <Button
              variant="outline"
              size="sm"
              color={isActive ? "gray" : "blue"}
              onClick={() => setOpened(true)}
              disabled={isPatching}
            >
              Endre status
            </Button>
          )}
        </Group>
      </Alert>

      <Modal opened={opened} onClose={closeModal} title={<Title order={3}>Endre malstatus</Title>} centered size="md">
        <Stack>
          <Text size="sm">
            Malen er <strong>{currentStatus}</strong>. Bekreft for å sette malen til <strong>{targetStatus}</strong>.
          </Text>

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeModal}>
              Avbryt
            </Button>
            <Button loading={isPatching} color={targetStatus === "AKTIV" ? "green" : "blue"} onClick={handleSubmit}>
              Bekreft endring
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default IssueBoardMcTemplateBanner;
